// src/pages/services/HajjUmrah.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const HajjUmrah = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "Excellent Travels made my Hajj journey truly spiritual and hassle-free. Their attention to detail and knowledge of the rituals was impressive.",
            name: "Ahmed Mahmoud",
            location: "Cairo, Egypt",
            avatar: "/images/testimonials/person5.jpg"
        },
        {
            content: "The Umrah package exceeded my expectations. The accommodations were close to the holy sites, and the guides were knowledgeable and supportive.",
            name: "Fatima Rahman",
            location: "Kuala Lumpur, Malaysia",
            avatar: "/images/testimonials/person6.jpg"
        }
    ];

    const faqs = [
        {
            question: "What is the difference between Hajj and Umrah?",
            answer: "Hajj is the annual Islamic pilgrimage to Mecca that is mandatory for all able Muslims once in their lifetime. Umrah is a shorter pilgrimage that can be performed at any time of the year and is not mandatory."
        },
        {
            question: "What documents do I need for Hajj/Umrah?",
            answer: "You'll need a valid passport, a Hajj/Umrah visa, proof of required vaccinations, and additional documentation depending on your country of origin. Our team will provide you with a complete checklist based on your specific situation."
        },
        {
            question: "When should I book my Hajj or Umrah package?",
            answer: "We recommend booking your Hajj package well in advance due to limited visa quotas and high demand. For Umrah, advance booking is advisable, especially during peak seasons. Contact us for current booking timelines."
        },
        {
            question: "Do you provide guidance on the rituals during Hajj and Umrah?",
            answer: "Yes, our packages include experienced guides who provide comprehensive instructions on all rituals. We also conduct pre-departure seminars and provide educational materials to help you prepare spiritually and practically."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="hajj-umrah" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="/Assets/image/kabba-1.jpg"
                    alt="Masjid al-Haram in Mecca - Hajj and Umrah"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Hajj & Umrah Services</h1>
                    <p className="service-subtitle">
                        Complete spiritual journey packages with expert guidance and support.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="/Assets/image/mosque-logo.png"
                alt="Hajj & Umrah Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2693/2693616.png" alt="Mosque Icon" className="section-icon" />
                        Embark on a Sacred Journey
                    </h2>
                    <p>
                        At Excellent Travels, we understand the profound spiritual significance of Hajj and Umrah.
                        Our specialized packages are designed to provide a seamless and meaningful pilgrimage experience,
                        allowing you to focus entirely on your spiritual journey.
                    </p>
                    <p>
                        Our Hajj and Umrah services include:
                    </p>
                    <ul>
                        <li>Comprehensive visa processing and documentation assistance</li>
                        <li>Premium accommodations in close proximity to the holy sites</li>
                        <li>Transportation between all religious sites and airports</li>
                        <li>Experienced religious guides to assist with rituals</li>
                        <li>Pre-departure orientation sessions</li>
                        <li>24/7 on-ground support throughout your journey</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="/Assets/image/kabba-1.jpg"
                            alt="Kaaba in Masjid al-Haram"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Experience the spiritual journey to the Holy Kaaba with our guided services</div>
                    </div>

                    <p>
                        We offer various package options to accommodate different preferences and budgets,
                        ensuring that every pilgrim can fulfill their religious obligation with dignity and comfort.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/5266/5266538.png" alt="Hajj Packages Icon" className="section-icon" />
                        Our Hajj Packages
                    </h2>
                    <p>
                        We offer several Hajj packages to meet different needs:
                    </p>
                    <ul>
                        <li><strong>Standard Package:</strong> Comfortable accommodations, all necessary transportation, and guided assistance</li>
                        <li><strong>Premium Package:</strong> Closer accommodations to Haram, enhanced meals, and additional services</li>
                        <li><strong>VIP Package:</strong> Luxury accommodations, private transportation, and personalized service</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="/Assets/image/masjid-nabawi-2.jpg"
                            alt="Masjid al-Haram in Mecca"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Our packages include accommodations near the holy sites for your convenience</div>
                    </div>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/5266/5266471.png" alt="Umrah Packages Icon" className="section-icon" />
                        Our Umrah Packages
                    </h2>
                    <p>
                        Our Umrah packages are available year-round and can be customized to include:
                    </p>
                    <ul>
                        <li>Short stays or extended spiritual journeys based on your preferences</li>
                        <li>Visits to historical Islamic sites in Makkah and Madinah</li>
                        <li>Family-friendly accommodations and services</li>
                        <li>Special packages during Ramadan</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="/Assets/image/medina-3.jpg"
                            alt="Masjid al-Nabawi in Medina"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Our Umrah packages include visits to the Prophet's Mosque in Medina</div>
                    </div>

                    <h2>
                        <img src="/Assets/image/mosque-logo.png" alt="Spiritual Preparation Icon" className="section-icon" />
                        Spiritual Preparation
                    </h2>
                    <p>
                        We believe that preparation is key to a fulfilling pilgrimage. Our services include:
                    </p>
                    <ul>
                        <li>Educational seminars on the rituals and their significance</li>
                        <li>Practical workshops on what to expect during your journey</li>
                        <li>Comprehensive guidebooks and materials</li>
                        <li>Access to scholars for personal questions and guidance</li>
                    </ul>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/5266/5266538.png"
                        alt="Kaaba Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Hajj/Umrah Services</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/3500/3500833.png" alt="License Icon" />
                            </span>
                            Licensed and approved by Ministry of Hajj
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693507.png" alt="Experience Icon" />
                            </span>
                            Over 15 years of experience
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693650.png" alt="Language Icon" />
                            </span>
                            Multilingual guides and support staff
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693574.png" alt="Package Icon" />
                            </span>
                            All-inclusive transparent packages
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693510.png" alt="Female Guide Icon" />
                            </span>
                            Dedicated female guides for women
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693523.png" alt="Medical Icon" />
                            </span>
                            Medical support throughout the journey
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2693/2693564.png" alt="Payment Icon" />
                            </span>
                            Flexible payment plans available
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Begin Your Sacred Journey?</h2>
                <p>
                    Contact our specialized Hajj and Umrah consultants to discuss your pilgrimage plans, receive personalized pricing, and find the package that best suits your spiritual needs.
                </p>
                <Link to="/contact" className="cta-button">
                    Request a Confidential Quote
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Pilgrims Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default HajjUmrah;